import { z } from 'zod';
import { FieldTemplateB64Schema } from './field.schema.js';

// ============================================================
// Device-token FIELD face enrollment (the APK enroll-push). Distinct from the
// JWT HR /enroll above: this rides the X-Device-Token rails the field app uses
// for /events, resolves the worker by code scoped to the device's worksite, runs
// a server-side 1:N dedup against OTHER workers, and is idempotent by `uuid`.
//
// NO consent boolean: paper consent is collected at registration (hard
// constraint), so the server stamps consentAt=now on the FIELD path.
// ============================================================
export const FieldDeviceEnrollRequestSchema = z
  .object({
    // Device idempotency key for THIS enrollment (a replay acks DUPLICATE).
    uuid: z.string().min(8).max(64),
    // Employee.employeeCode — the roster syncs to the device by code.
    workerCode: z.string().min(1).max(32),
    templates: z.array(FieldTemplateB64Schema).min(1).max(10),
    /* The capturing operator (audit trail; resolved to enrolledByOperatorId).
     *
     * OPTIONAL ONLY IN THE SENSE THAT `unattended` CAN REPLACE IT. It was
     * required, and a wall panel cannot satisfy it — having no operator is the
     * defining property of an unattended gate, not an omission. But simply
     * relaxing it to `.optional()` would be a regression on the handheld path: a
     * device that dropped the field through a bug would produce an enrolment
     * whose consent record says "nobody", indistinguishable from a panel's, and
     * nothing would complain. The refinement below therefore demands EXACTLY ONE
     * of this field and `unattended`. */
    operatorCode: z.string().min(1).max(32).optional(),
    /* NOBODY IS STANDING HERE — said out loud, by the device, on every request.
     *
     * True means this capture had no human attesting it: the device is running
     * as an unattended panel and the face is the only witness. It is stored on
     * the template row (`capturedUnattended`) so a consent trail can tell a
     * designed absence of an operator from a lost one.
     *
     * Note what this does and does not buy. The device token is the device's
     * identity, so `unattended: true` and `operatorCode: 'OP-1'` are equally
     * self-asserted by the same authenticated caller and neither is verified.
     * What the flag adds is that the AMBIGUOUS state — no operator, no
     * declaration — is now impossible, and that a handheld quietly enrolling
     * unattended is a fact recorded on every row it writes rather than an
     * inference from a null. */
    unattended: z.boolean().optional(),
    // Best-frame quality score at capture (optional; audit).
    quality: z.number().min(0).max(1).optional(),
    // Graded liveness score at capture (optional; audit).
    liveness: z.number().min(0).max(1).optional(),
    /* The engine that embedded `templates`, stamped on the draft when it was
     * queued. Optional for wire compatibility; when present it must be the
     * server's engine or the push is refused 409 FACE_MODEL_OUTDATED. The
     * device header says what the app runs NOW; this says what made the face. */
    modelId: z.string().min(1).max(64).optional(),
    /* Operator override of a 1:N dedup HARD-BLOCK. When false and the incoming
     * face matches a DIFFERENT enrolled worker, the server returns 409
     * DEDUP_BLOCKED so the device shows the override dialog; a re-POST with true
     * proceeds and is recorded in the audit trail.
     *
     * REFUSED OUTRIGHT FROM AN UNATTENDED DEVICE (see the refinement). An
     * override is a named person taking responsibility for enrolling a face the
     * server believes belongs to somebody else; a panel has no such person, so an
     * override from one is not a weaker override, it is a forged one. Silently
     * proceeding is how one worker's face ends up on another worker's payroll
     * record — and the auto-harvest loop would then keep confirming it. */
    overrideDedup: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const unattended = v.unattended === true;
    if (unattended && v.operatorCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operatorCode'],
        message:
          'An unattended enrolment cannot name a capturing operator. Send ' +
          'operatorCode (attended) or unattended: true, never both.',
      });
    }
    if (!unattended && !v.operatorCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operatorCode'],
        message:
          'operatorCode is required unless the device declares unattended: true. ' +
          'A missing operator must be a statement that nobody was there, not a ' +
          'gap in the consent record.',
      });
    }
    if (unattended && v.overrideDedup) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overrideDedup'],
        message:
          'An unattended device cannot override a duplicate-face block: there is ' +
          'nobody whose override it would be. The block needs a human, and one ' +
          'has been notified.',
      });
    }
  });
export type FieldDeviceEnrollRequest = z.infer<typeof FieldDeviceEnrollRequestSchema>;

// RAW response (no {data} envelope) — the wire contract the field app codes
// against. The route maps a 409 DEDUP_BLOCKED to this same union so the device
// reads `status` to branch (override dialog vs. success vs. idempotent replay).
export const FieldDeviceEnrollResponseSchema = z.union([
  z.object({
    status: z.literal('ENROLLED'),
    templates: z.number().int().nonnegative(),
    version: z.number().int().positive(),
    /* Enrolled, but the face resembles another worker below the block rule
     * (faceDedupRule.ts WARN). HR has been asked to look; the device shows one
     * non-blocking line. Absent when nothing resembled anybody. */
    possibleDuplicate: z.object({ workerCode: z.string(), score: z.number() }).optional(),
  }),
  // Same uuid already processed (idempotent replay).
  z.object({ status: z.literal('DUPLICATE') }),
  // Face matches a DIFFERENT enrolled worker.
  z.object({
    status: z.literal('DEDUP_BLOCKED'),
    matchedWorkerCode: z.string(),
    score: z.number(),
    /* WHAT THE DEVICE IS SUPPOSED TO DO ABOUT IT — the half a 409 never carried.
     *
     * `DEDUP_BLOCKED` was written for a handheld: show the operator a dialog,
     * let them override, re-POST. A wall panel cannot show anyone anything, so
     * for it the same 409 has to mean something else entirely, and the device
     * must not have to infer which from its own local state.
     *
     * OPERATOR_OVERRIDE — today's meaning, unchanged for every attended device:
     *   a person is standing there, show them the dialog, a re-POST with
     *   overrideDedup: true proceeds and is audited.
     *
     * HR_REVIEW — TERMINAL. Do not retry, do not override, drop the enrolment
     *   and tell the worker to see the site office. Retrying cannot help (the
     *   gallery will match again) and an unattended override is refused by the
     *   request schema anyway. The server has raised the block to HR/Admin as a
     *   notification, so the block reaches a human even though the panel has no
     *   screen to raise it on — otherwise a worker stands at a gate that will
     *   never enrol them and nobody ever finds out why.
     *
     * The status and the HTTP code stay 409 for both: forking the status per
     * caller would fork the wire contract the deployed fleet is coded against,
     * for a distinction this field already carries. */
    resolution: z.enum(['OPERATOR_OVERRIDE', 'HR_REVIEW']),
  }),
]);
export type FieldDeviceEnrollResponse = z.infer<typeof FieldDeviceEnrollResponseSchema>;

// Per-worker FIELD face-enrollment workflow state, surfaced in /sync (so the APK
// roster can flag not-enrolled workers) and on the web employee DTOs. Mirrors the
// Prisma FieldEnrollmentStatus enum.
export const FieldEnrollmentStatusSchema = z.enum([
  'NOT_ENROLLED',
  'PENDING_SYNC',
  'ENROLLED',
]);
export type FieldEnrollmentStatus = z.infer<typeof FieldEnrollmentStatusSchema>;
